import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getMyEvaluationResults,
  getMyPendingEvaluations,
  getMySubmittedEvaluations,
  submitEvaluation,
} from "../../api/evaluations";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import EvaluationComparisonChart from "../common/EvaluationComparisonChart";
import "./MyEvaluations.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function raterTypeLabel(type) {
  if (type === "MANAGER") return "مدير مباشر";
  if (type === "PEER") return "زميل";
  if (type === "SUBORDINATE") return "مرؤوس";
  if (type === "SELF") return "تقييم ذاتي";
  return "مقيم";
}

function canEditTask(task, role) {
  if (typeof task?.canEdit === "boolean") return task.canEdit;
  const status = task?.subject?.round?.status;
  if (status === "ACTIVE") return true;
  return status === "CLOSED" && role === "gm";
}

function raterNotes(rater, questions) {
  const byId = Object.fromEntries(
    (questions || []).map((q) => [q.id, q.text]),
  );
  const general = String(rater?.comment || "").trim();
  const answerNotes = (rater?.answers || [])
    .filter((a) => String(a.comment || "").trim())
    .map((a) => ({
      question: byId[a.questionId] || "سؤال",
      comment: String(a.comment).trim(),
    }));
  return {
    general,
    answerNotes,
    hasAny: Boolean(general || answerNotes.length),
  };
}

function hasDetachedNotes(notes) {
  return Boolean(notes?.general?.length || notes?.perQuestion?.length);
}

function DetachedNotesSection({ title, hint, notes }) {
  if (!hasDetachedNotes(notes)) return null;
  return (
    <div className="my-evaluations__notes-block">
      <h3 className="my-evaluations__notes-title">{title}</h3>
      {hint ? <p className="my-evaluations__notes-hint">{hint}</p> : null}
      {(notes.general || []).map((comment, index) => (
        <div
          className="my-evaluations__notes"
          key={`general-${index}`}
        >
          {comment}
        </div>
      ))}
      {(notes.perQuestion || []).map((row) => (
        <div key={row.questionId} className="my-evaluations__notes-q">
          <strong>{row.text}</strong>
          {(row.comments || []).map((comment, index) => (
            <div
              className="my-evaluations__notes"
              key={`${row.questionId}-${index}`}
            >
              {comment}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function MyEvaluations() {
  const { role } = useAuth();
  const { refreshData } = useData();
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [submitted, setSubmitted] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [activeTask, setActiveTask] = useState(null);
  const [formMode, setFormMode] = useState("submit");
  const [selectedSubmitted, setSelectedSubmitted] = useState(null);
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});
  const [generalComment, setGeneralComment] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [pendingRows, submittedRows, resultRows] = await Promise.all([
        getMyPendingEvaluations(),
        getMySubmittedEvaluations(),
        getMyEvaluationResults(),
      ]);
      setPending(Array.isArray(pendingRows) ? pendingRows : []);
      setSubmitted(Array.isArray(submittedRows) ? submittedRows : []);
      setResults(Array.isArray(resultRows) ? resultRows : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل التقييمات"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fillForm = (task, mode) => {
    const taskQuestions = task.subject?.round?.questions || [];
    const nextScores = {};
    const nextComments = {};
    taskQuestions.forEach((q) => {
      const answer = (task.answers || []).find((a) => a.questionId === q.id);
      nextScores[q.id] =
        mode === "edit" && answer?.score != null ? String(answer.score) : "";
      nextComments[q.id] =
        mode === "edit" ? String(answer?.comment || "") : "";
    });
    setScores(nextScores);
    setComments(nextComments);
    setGeneralComment(mode === "edit" ? String(task.comment || "") : "");
    setFormMode(mode);
    setActiveTask(task);
    setSelectedSubmitted(null);
    setError("");
    setSuccess("");
  };

  const openTask = (task) => fillForm(task, "submit");

  const openEdit = (task) => {
    if (!canEditTask(task, role)) return;
    fillForm(task, "edit");
  };

  const questions = useMemo(
    () => activeTask?.subject?.round?.questions || [],
    [activeTask],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeTask) return;
    setError("");
    setSuccess("");

    for (const q of questions) {
      const score = Number(scores[q.id]);
      if (!Number.isFinite(score) || score < 0 || score > Number(q.maxScore)) {
        setError(`أدخل درجة صحيحة لـ: ${q.text} (0–${q.maxScore})`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await submitEvaluation(activeTask.id, {
        answers: questions.map((q) => ({
          questionId: q.id,
          score: Number(scores[q.id]),
          comment: comments[q.id]?.trim() || undefined,
        })),
        comment: generalComment.trim() || undefined,
      });
      setSuccess(
        formMode === "edit" ? "تم تحديث التقييم" : "تم إرسال التقييم بنجاح",
      );
      setActiveTask(null);
      setFormMode("submit");
      await load();
      refreshData?.({ silent: true, inboxOnly: true });
      setTab("submitted");
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          formMode === "edit" ? "تعذر حفظ التعديل" : "تعذر إرسال التقييم",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="my-evaluations" dir="rtl">
      <div className="ess-home-header">
        <div>
          <h1 className="ess-home-title">تقييم الأداء 360 درجة</h1>
          <p className="ess-home-subtitle">
            يمكنك تعديل التقييم الذي أرسلته قبل إغلاق الجولة. بعد الإغلاق،
            المدير العام فقط يستطيع تعديل تقييمه. ملاحظات المقيمين تظهر بدون
            اسم الكاتب.
          </p>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="my-evaluations__tabs">
        <button
          type="button"
          className={tab === "pending" ? "is-active" : ""}
          onClick={() => {
            setTab("pending");
            setActiveTask(null);
            setSelectedSubmitted(null);
          }}
        >
          تقييمات مطلوبة ({pending.length})
        </button>
        <button
          type="button"
          className={tab === "submitted" ? "is-active" : ""}
          onClick={() => {
            setTab("submitted");
            setActiveTask(null);
            setSelectedSubmitted(null);
          }}
        >
          ما أرسلته ({submitted.length})
        </button>
        <button
          type="button"
          className={tab === "results" ? "is-active" : ""}
          onClick={() => {
            setTab("results");
            setActiveTask(null);
            setSelectedSubmitted(null);
          }}
        >
          نتيجتي ({results.length})
        </button>
      </div>

      {loading ? (
        <p className="text-muted">جاري التحميل...</p>
      ) : activeTask ? (
          <form className="my-evaluations__panel" onSubmit={handleSubmit}>
            <button
              type="button"
              className="btn btn-link px-0 mb-2"
              onClick={() => {
                setActiveTask(null);
                setFormMode("submit");
              }}
            >
              ← رجوع للقائمة
            </button>
            <h2 className="my-evaluations__panel-title">
              {formMode === "edit" ? "تعديل التقييم — " : ""}
              {activeTask.raterType === "SELF"
                ? "تقييمك الذاتي (كل الأسئلة)"
                : `تقييم: ${activeTask.subject?.employee?.fullName}`}
            </h2>
            <p className="text-muted">
              الجولة: {activeTask.subject?.round?.title} — دورك:{" "}
              {raterTypeLabel(activeTask.raterType)}
              {activeTask.raterType === "SELF"
                ? " — تجيب على جميع أسئلة الجولة"
                : ""}
              {formMode === "edit" &&
              activeTask.subject?.round?.status === "CLOSED"
                ? " — الجولة مغلقة، والتعديل متاح لك كمدير عام"
                : formMode === "edit"
                  ? " — التعديل متاح حتى إغلاق الجولة"
                  : ""}
            </p>

            {questions.map((q) => (
              <div className="my-evaluations__q-block" key={q.id}>
                <label className="form-label">
                  {q.text}{" "}
                  <span className="text-muted">
                    (وزن {Number(q.weightPercent)}% — من {q.maxScore})
                  </span>
                </label>
                <div className="row g-2">
                  <div className="col-md-4">
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      max={q.maxScore}
                      step="1"
                      value={scores[q.id] ?? ""}
                      onChange={(e) =>
                        setScores((prev) => ({
                          ...prev,
                          [q.id]: e.target.value,
                        }))
                      }
                      placeholder={`0 – ${q.maxScore}`}
                      required
                    />
                  </div>
                  <div className="col-md-8">
                    <input
                      className="form-control"
                      placeholder="ملاحظة اختيارية على السؤال"
                      value={comments[q.id] ?? ""}
                      onChange={(e) =>
                        setComments((prev) => ({
                          ...prev,
                          [q.id]: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="mb-3">
              <label className="form-label">ملاحظة عامة (اختياري)</label>
              <textarea
                className="form-control"
                rows={3}
                value={generalComment}
                onChange={(e) => setGeneralComment(e.target.value)}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting
                ? "جاري الحفظ..."
                : formMode === "edit"
                  ? "حفظ التعديل"
                  : "إرسال التقييم"}
            </button>
          </form>
      ) : tab === "pending" ? (
        pending.length === 0 ? (
          <div className="my-evaluations__panel">
            <p className="text-muted mb-0">لا توجد تقييمات مطلوبة حالياً</p>
          </div>
        ) : (
          <div className="my-evaluations__list">
            {pending.map((task) => (
              <button
                type="button"
                key={task.id}
                className="my-evaluations__card"
                onClick={() => openTask(task)}
              >
                <strong>
                  {task.raterType === "SELF"
                    ? "تقييمك الذاتي"
                    : task.subject?.employee?.fullName}
                </strong>
                <span className="text-muted">
                  {task.subject?.round?.title} — {raterTypeLabel(task.raterType)}
                  {task.raterType === "SELF"
                    ? ` — ${(task.subject?.round?.questions || []).length} سؤال`
                    : ""}
                </span>
                <span className="my-evaluations__cta">بدء التقييم</span>
              </button>
            ))}
          </div>
        )
      ) : tab === "submitted" ? (
        selectedSubmitted ? (
          <div className="my-evaluations__panel">
            <button
              type="button"
              className="btn btn-link px-0 mb-2"
              onClick={() => setSelectedSubmitted(null)}
            >
              ← رجوع للقائمة
            </button>
            <h2 className="my-evaluations__panel-title">
              {selectedSubmitted.raterType === "SELF"
                ? "تقييمك الذاتي"
                : `تقييم: ${selectedSubmitted.subject?.employee?.fullName}`}
            </h2>
            <p className="text-muted">
              الجولة: {selectedSubmitted.subject?.round?.title} — دورك:{" "}
              {raterTypeLabel(selectedSubmitted.raterType)}
              {selectedSubmitted.scorePercent != null
                ? ` — نتيجتك: ${Number(selectedSubmitted.scorePercent)}%`
                : ""}
            </p>
            {(selectedSubmitted.subject?.round?.questions || []).map((q) => {
              const answer = (selectedSubmitted.answers || []).find(
                (a) => a.questionId === q.id,
              );
              return (
                <div className="my-evaluations__q-block" key={q.id}>
                  <label className="form-label">
                    {q.text}{" "}
                    <span className="text-muted">
                      (وزن {Number(q.weightPercent)}% — من {q.maxScore})
                    </span>
                  </label>
                  <div className="my-evaluations__submitted-score">
                    الدرجة:{" "}
                    {answer?.score != null
                      ? `${answer.score} / ${q.maxScore}`
                      : "—"}
                  </div>
                  {answer?.comment ? (
                    <div className="my-evaluations__notes">
                      <strong>ملاحظتك: </strong>
                      {answer.comment}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {selectedSubmitted.comment ? (
              <div className="my-evaluations__notes">
                <strong>ملاحظة عامة: </strong>
                {selectedSubmitted.comment}
              </div>
            ) : null}
            {!selectedSubmitted.comment &&
            !raterNotes(
              selectedSubmitted,
              selectedSubmitted.subject?.round?.questions,
            ).hasAny ? (
              <p className="text-muted mb-0">لا توجد ملاحظات على هذا التقييم.</p>
            ) : null}
            {canEditTask(selectedSubmitted, role) ? (
              <div className="my-evaluations__actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => openEdit(selectedSubmitted)}
                >
                  تعديل التقييم
                </button>
                <span className="text-muted small align-self-center">
                  {selectedSubmitted.subject?.round?.status === "CLOSED"
                    ? "الجولة مغلقة، ويمكنك تعديل تقييمك لأنك المدير العام."
                    : "يمكنك تعديل هذا التقييم حتى إغلاق الجولة."}
                </span>
              </div>
            ) : selectedSubmitted.subject?.round?.status === "CLOSED" ? (
              <p className="text-muted mb-0 mt-3">
                لا يمكن تعديل التقييم بعد إغلاق الجولة.
              </p>
            ) : null}
          </div>
        ) : submitted.length === 0 ? (
          <div className="my-evaluations__panel">
            <p className="text-muted mb-0">لم ترسل أي تقييم بعد</p>
          </div>
        ) : (
          <div className="my-evaluations__list">
            {submitted.map((task) => {
              const notes = raterNotes(task, task.subject?.round?.questions);
              return (
                <button
                  type="button"
                  key={task.id}
                  className="my-evaluations__card"
                  onClick={() => setSelectedSubmitted(task)}
                >
                  <strong>
                    {task.raterType === "SELF"
                      ? "تقييمك الذاتي"
                      : task.subject?.employee?.fullName}
                  </strong>
                  <span className="text-muted">
                    {task.subject?.round?.title} —{" "}
                    {raterTypeLabel(task.raterType)}
                    {task.scorePercent != null
                      ? ` — ${Number(task.scorePercent)}%`
                      : ""}
                  </span>
                  <span className="my-evaluations__cta">
                    {canEditTask(task, role)
                      ? "عرض أو تعديل التقييم"
                      : notes.hasAny
                        ? "عرض ملاحظاتك"
                        : "عرض التقييم المُرسل"}
                  </span>
                </button>
              );
            })}
          </div>
        )
      ) : results.length === 0 ? (
        <div className="my-evaluations__panel">
          <p className="text-muted mb-0">
            لا توجد نتائج بعد. تظهر النتيجة بعد إغلاق الجولة.
          </p>
        </div>
      ) : (
        <div className="my-evaluations__list">
          {results.map((subject) => (
            <div className="my-evaluations__result-card" key={subject.id}>
              <div className="my-evaluations__result-head">
                <div>
                  <strong>{subject.round?.title}</strong>
                  <div className="text-muted small">
                    {(subject.round?.questions || []).length} أسئلة
                  </div>
                </div>
                <div className="my-evaluations__final">
                  {subject.finalPercent != null
                    ? `${Number(subject.finalPercent)}%`
                    : "—"}
                </div>
              </div>

              <EvaluationComparisonChart
                perspective="self"
                comparison={subject.comparison}
                questions={subject.round?.questions}
                raters={subject.raters}
              />

              <DetachedNotesSection
                title="ملاحظاتك"
                notes={subject.selfNotes}
              />
              <DetachedNotesSection
                title="ملاحظات المقيمين"
                hint="تظهر الملاحظات بدون اسم الكاتب."
                notes={subject.anonymousNotes}
              />
              {!hasDetachedNotes(subject.selfNotes) &&
              !hasDetachedNotes(subject.anonymousNotes) ? (
                <div className="text-muted small mt-2">
                  لا توجد ملاحظات على هذه الجولة.
                </div>
              ) : null}

              <div className="text-muted small mt-2">
                عدد المقيمين المكتملين: {(subject.raters || []).length}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
